import { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FilterMatchMode } from "primereact/api";
import { BoxFull } from "iuvity-ui-components/dist/boxfull";
import { DataTable } from "iuvity-ui-components/dist/datatable";
import { TableHeader } from "../../components/datatable/TableHeader";
import { FormSkeleton } from "../../components";
import {
  CustomDialog,
  DialogProps,
} from "../../components/dialog/CustomDialog";
import { AppContext } from "../../context/index";
import { useForm } from "react-hook-form";
import { Column } from 'iuvity-ui-components/dist/column';
import { Actions } from "../datatable/Actions";
import { AccessButton, Accordion, Calendar } from "iuvity-ui-components";
import { useTenants } from "../../hooks/useTenants";
import { getRolesByTenant } from "../../services/console/roles";

const formBlankValues = {
  objective: '',
  segment: '',
  environment: '',
};

interface ApplicationRolesProps {
  data?: any;
  rolesSelection?: any;
  setRolesSelection?: any;
  isButtonAppRoles?: boolean;
  isButtonAdminRoles?: boolean;
  userRoleData?: any;
  isUpdate?: boolean;
  onSelectRole?:any;
};

export const ApplicationRolesComponent: React.FC<ApplicationRolesProps> = ({
  data,
  rolesSelection,
  setRolesSelection,
  isButtonAppRoles,
  isButtonAdminRoles,
  userRoleData,
  isUpdate,
  onSelectRole

}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    t,
    appState: { language, selectedApplicationRoles, applicationRolesDataFormated, tenantParentName, selectedTenantId, tenantId },
    setAppState
  } = useContext(AppContext);
  const [selectedRoles, setSelectedRoles] = useState<any[]>(selectedApplicationRoles);
  const dataTenantsList = data;

  const form = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: formBlankValues,
  });

  const columnsApps = useMemo(() => {
    if (language) {
      return [
        {
          field: "select",
          header: ''
        },
        {
          field: "objective",
          header: t("Users.objectiveLabel"),
        },
        {
          field: "segment",
          header: t("Users.segmentLabel"),
        },
        {
          field: "environment",
          header: t("Users.segmentLabel"),
        },
      ];
    } else {
      return [];
    }
  }, [t, language]);

  const columnsRoles = useMemo(() => {
    if (language) {
      return [
        {
          field: "select",
          header: "",
        },
        {
          field: "name",
          header: t("Users.rolNameLabel"),
        },
        {
          field: "roleTypeId",
          header: t("Users.rolTypeIdLabel"),
        },
        {
          field: "expeditionDate",
          header: t("Users.expeditionDateLabel"),
        },
        {
          field: "expirationDate",
          header: t("Users.expirationDateLabel"),
        },
        {
          field: "actionsDetail",
          header: "",
        },
        {
          field: "actions",
          header: t("Users.actionsLabel"),
        }
      ];
    } else {
      return [];
    }
  }, [t, language]);

  const defaultFilterValues = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  };

  const [applicationRolesData, setApplicationRolesData] = useState<any[]>([]);
  const [selectedColumnsApps, setSelectedColumnsApps] = useState(columnsApps);
  const [selectedColumnsRoles, setSelectedColumnsRoles] = useState(columnsRoles);
  const [filtersApps, setFiltersApps] = useState(defaultFilterValues);
  const [filtersRoles, setFiltersRoles] = useState(defaultFilterValues);
  const [expandedRows, setExpandedRows] = useState();
  const [filteredData, setFilteredData] = useState<any | null>(null);
  const [roleSelectionRow, setRoleSelectionRow] = useState<boolean>(true);

  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);

  useEffect(() => {
    if (language)
      setSelectedColumnsApps((prev) =>
        prev.map((col) => ({
          field: col.field,
          header: (col.field === 'select' || col.field === 'actionsDetail') ? '' : t(`Users.${col.field}Label`),
        }))
      );
    setSelectedColumnsRoles((prev) =>
      prev.map((col) => ({
        field: col.field,
        header: (col.field === 'select' || col.field === 'editor' || col.field === 'actionsDetail') ? ' ' : t(`Users.${col.field}Label`),
      }))
    );
  }, [t, language, setSelectedColumnsApps, setSelectedColumnsRoles]);

  const actionDetailFn = (label: any) => {
    setAppState((prev) => ({
      ...prev,
      selectedRole: label,
    }));
    navigate('/users/detail-rol');
    window.scrollTo(0, 0);
  };

  const allowExpansion = (rowData: any) => {
    if (rowData) {
      return true;
    }
    return false;
  };

  const onRowEditComplete = (e: any) => {
    let { newData, index } = e;
    const { appId, environmentId, roleId, tenantId } = newData;
    const tenantIndex = applicationRolesData.findIndex((tenant: any) => tenant.id === tenantId);
    let tenant: any = applicationRolesData[tenantIndex];
    const segmentIndex = tenant.segmentsFormated.findIndex((segment: any) => segment.appId == appId && segment.environmentId == environmentId);
    let segment = tenant.segmentsFormated[segmentIndex];
    const roleIndex = segment.roles.findIndex((role: any) => role.roleId === roleId);

    let expeditionDate = null;
    if (newData.expeditionDate && typeof newData.expeditionDate == 'string') {
      expeditionDate = newData.expeditionDate;
    } else if (newData.expeditionDate) {
      expeditionDate = newData.expeditionDate.toLocaleDateString('en');
    }
    let expirationDate = null;
    if (newData.expirationDate && typeof newData.expirationDate == 'string') {
      expirationDate = newData.expirationDate;
    } else if (newData.expirationDate) {
      expirationDate = newData.expirationDate.toLocaleDateString('en');
    }

    const newDataRol = {
      ...newData,
      expeditionDate,
      expirationDate
    }
    tenant.segmentsFormated[segmentIndex].roles[roleIndex] = newDataRol;
    let newApplicationRolesData = [...applicationRolesData];
    newApplicationRolesData[tenantIndex] = tenant;
    setApplicationRolesData(newApplicationRolesData);

    const indexRoleSelected = selectedApplicationRoles.findIndex((role: any) => role.roleId == roleId && role.appId == appId && role.environmentId == environmentId);
    if (indexRoleSelected >= 0) {
      const roleSelected = selectedApplicationRoles[indexRoleSelected];
      roleSelected.expeditionDate = expeditionDate;
      roleSelected.expirationDate = expirationDate;

      selectedApplicationRoles[indexRoleSelected] = roleSelected;
      setAppState((prev) => ({
        ...prev,
        selectedApplicationRoles: selectedApplicationRoles
      }));
    }
  };

  const calendarEditor = (options: any) => {
    if (options.field === 'expeditionDate' || options.field === 'expirationDate') {
      let value: any = options.value;
      if (value && typeof value == 'string') {
        value = new Date(value);
      }

      return (
        <Calendar
          label={''}
          value={value}
          onChange={(e) => { options.editorCallback(e.target.value) }}
          dateFormat="mm/dd/yy"
          placeholder="mm/dd/yyyy"
          mask="99/99/9999"
          className="mt-2 mb-2"
        />
      )
    } else {
      return options.value;
    }
  };

  const rowExpansionTemplate1 = (data: any) => {
    let dataRoles: any[] = [];
    const { appId, environmentId, tenantId } = data;
    const tenantIndex = applicationRolesData.findIndex((tenant: any) => tenant.id === tenantId);
    let tenant: any = applicationRolesData[tenantIndex];
    const segmentIndex = tenant.segmentsFormated.findIndex((segment: any) => segment.appId == appId && segment.environmentId == environmentId);
    let segment = tenant.segmentsFormated[segmentIndex];
    dataRoles = segment.roles;
    const rowClassName = (data: any) => ((location.pathname === '/users/detail' && data) ? 'p-disabled' : '');

    return (
      <div className={`application-roles-table ${location.pathname === '/users/detail' && 'roles-row-disabled'}`}>
        <BoxFull className="my-4 mx-0">
          <div className="application-roles-title">
            <div>{t("Users.applicationRoles")}: {data.objective} - {data.segment} - {data.environment}</div>
          </div>
          {dataRoles ? (
            <DataTable
              id="userpagetabview-applications-datatable"
              header={
                <TableHeader
                  title={t("Users.internalUsersTitle")}
                  columns={columnsRoles}
                  selectedColumns={selectedColumnsRoles}
                  toggleColumns={setSelectedColumnsRoles}
                  filters={filtersRoles}
                  setFilters={setFiltersRoles}
                  // reload={reloadFn}
                  hasCheckColumn
                />
              }
              currentPageReportTemplate={t(
                "ConsoleTranslations.rowsPaginatorTemplateLabel"
              )}
              emptyMessage={t("TableActions.emptyMessage")}
              paginator={false}
              size="normal"
              editMode="row"
              dataKey="id"
              sortField="functionality"
              sortOrder={1}
              value={dataRoles?.map((register)=>({
                ...register,
                actionsDetail: (
                  <Actions
                    viewButtonEdit={false}
                    viewButtonDelete={false}
                    onDetail={async () => {
                      const { data: dataRolesByTenant } = await getRolesByTenant(register?.tenantId ?? 0);
                      const role = dataRolesByTenant?.find((elem:any) => elem?.id === register?.roleId);
                      const tenantName = dataTenantsList?.find((elem:any) => elem?.id === register?.tenantId)?.name;

                      setAppState((prev) => ({
                        ...prev,
                        selectedRolesObject: {
                          ...role,
                          tenantName,
                          cameFromRolesPage: true,
                        },
                      }));

                      navigate("/adminroles/detailroles");
                    }}
                  />
                ),
              }))}
              scrollable
              resizableColumns
              selectOnEdit={false}
              selectionMode={'checkbox'}
              selection={selectedApplicationRoles}
              // onSelectionChange={(e) => setAppState((prev) => ({
              //   ...prev,
              //   selectedApplicationRoles: e.value
              // }))}

              onSelectionChange={(e) => {
                onSelectRole(true);
                if (e.type == 'all') {
                  const roleIds = dataRoles?.map((role: any) => role.id);
                  let currentSelectedAplicationRoles: any[] = [];
                  if (e.value.length == 0) {
                    currentSelectedAplicationRoles = selectedApplicationRoles.filter((selectedRole: any) => !roleIds.includes(selectedRole.id));
                  } else {
                    currentSelectedAplicationRoles = [...selectedApplicationRoles, ...e.value];
                  }
                  currentSelectedAplicationRoles = Object.values(
                    currentSelectedAplicationRoles.reduce((acc, obj) => ({ ...acc, [obj.id]: obj }), {})
                  )
                  setAppState((prev) => ({
                    ...prev,
                    selectedApplicationRoles: currentSelectedAplicationRoles
                  }))
                } else {
                  setAppState((prev) => ({
                    ...prev,
                    selectedApplicationRoles: e.value
                  }))
                }
              }}
              rowHover
              onRowEditComplete={(e) => onRowEditComplete(e)}
              rowClassName={rowClassName}
            >
              {selectedColumnsRoles.map((col) => (
                <Column
                  key={col.field}
                  field={col.field}
                  header={col.header}
                  sortable={!(col.field === "actions" || col.field === "select" || col.field === "editor" || col.field === "actionsDetail")}
                  align={col.field === "actions" ? "center" : "left"}
                  headerStyle={{
                    justifyContent: col.field === "actions" ? "center" : undefined,
                    width: col.field === "actionsDetail" ? "60px" : undefined,
                  }}
                  style={{
                    boxShadow: (col.field === "actionsDetail")
                      ? "rgb(230 230 230 / 55%) -2px 0px, rgb(235 235 235 / 55%) -3px 0px, rgb(240 240 240 / 45%) -5px 0px"
                      : undefined,
                    maxWidth: (col.field === "actions") ? "102px" : (col.field === "editor" || col.field === "actionsDetail") ? '60px' : undefined,
                    whiteSpace: "normal",
                    minWidth: col.field === "select" ? "3.5rem" : (col.field === "actions") ? "102px" :
                      (col.field === "editor" || col.field === "actionsDetail") ? "60px" : "14rem",
                  }}
                  frozen={(col.field === "actionsDetail") ? true : false}
                  alignFrozen={col.field === "actions" ? "right" : undefined}
                  selectionMode={col.field === "select" ? 'multiple' : undefined}
                  editor={(options) => calendarEditor(options)}
                  rowEditor={col.field === "actions" ? true : false}
                  resizeable
                />
              ))}
            </DataTable>
          ) : (
            <FormSkeleton length={8} />
          )}
        </BoxFull>
      </div>
    )
  };

  useEffect(() => {
    // var iconEditRow = document.getElementsByClassName("p-row-editor-init-icon");
    // for (var i = 0; i < iconEditRow.length; i++) {
    //   iconEditRow[i].className = iconEditRow[i].className.replaceAll('pi-pencil', '');
    //   iconEditRow[i].className += " iu iu-icon-edit";
    // }
    // var iconEditRow = document.getElementsByClassName("p-row-editor-init");
    // for(var i = 0; i < iconEditRow.length; i++) {
    //   iconEditRow[i].className += " p-disabled";
    // }
  }, [expandedRows]);

  useMemo(() => {
    if (data) {
      let newData: any = [...data];
      newData = newData.sort((a: any, b: any) => a.name.localeCompare(b.name));
      let indexTenantMain = newData.findIndex((tenant: any) => tenant.id === (selectedTenantId ? selectedTenantId : tenantId));
      if (indexTenantMain >= 0) {
        let main = newData[indexTenantMain];
        newData.splice(indexTenantMain, 1);
        newData.unshift(main);
      }
      let tenantAndRoles = [];
      for (let tenant of newData) {
        let segments = tenant.segments;
        if (!tenant.segmentsFormated) {


          let roles = tenant?.roles
            .filter((rol: any) => rol.isAdministrative != true && rol.statusId !== "DELETED")
            .sort((a: any, b: any) => a.name.localeCompare(b.name))
            .map((role: any) => {
              return { ...role, expeditionDate: null, expirationDate: null }
            });;

          let segmentsAndRoles = segments.flatMap((segment: any) => {
            return segment.environments.flatMap((env: any) => {
              const rolesFilter = roles.map((rol: any) => {
                const selectedRole = selectedRoles.find((selectedRole: any) => selectedRole.roleId == rol.id && selectedRole.appId == segment.appid && selectedRole.environmentId == env.environmentId);
                let roleFormated = {
                  ...rol,
                  id: `${env.environmentId}_${rol.id}_${segment.appid}`,
                  roleId: rol.id,
                  environmentId: env.environmentId,
                  appId: segment.appid
                }
                if (selectedRole) {
                  roleFormated = {
                    ...roleFormated,
                    expeditionDate: selectedRole.expeditionDate ? (new Date(selectedRole.expeditionDate)).toLocaleDateString('en') : null,
                    expirationDate: selectedRole.expirationDate ? (new Date(selectedRole.expirationDate)).toLocaleDateString('en') : null,
                  }
                }
                return roleFormated;
              });
              return {
                id: env.environmentId,
                tenantId: tenant.id,
                appId: segment.appid,
                environmentId: env.environmentId,
                objective: segment.segmentname,
                segment: segment.segment,
                environment: env.name,
                roles: rolesFilter,
              }
            });

          });


          function getSegmentIndex(name: any) {
            switch (name) {
              case "RETAIL":
                return -1;
              case "CORPORATE":
                return 0;
              default:
                return 1;
            }
          }

          function getEnvIndex(env: any) {
            switch (env) {
              case "DEV":
                return -1;
              case "SIT":
                return 0;
              default:
                return 1;
            }
          }

          segmentsAndRoles.sort((a: any, b: any) => {
            const nameOrder = getSegmentIndex(a.segment) - getSegmentIndex(b.segment);
            if (nameOrder !== 0) return nameOrder;

            return getEnvIndex(a.environment) - getEnvIndex(b.environment);
          });
          // let newRoles: any[] = [];
          // tenant.segments.map((segment: any) => {
          //   const appId = segment.appid;
          //   segment.environments.map(({ environmentId }: any) => {
          //     roles.map((role: any) => {
          //       newRoles.push([{
          //         ...role,
          //         id: appId + '-' + environmentId + '-' + role.id,
          //         roleId: role.id,
          //         environmentId,
          //         appId

          //       }])

          //     })

          //   });

          // });
          tenant.segmentsFormated = segmentsAndRoles;
        }
        tenantAndRoles.push(tenant);
      }
      setApplicationRolesData(tenantAndRoles);
    }
  }, [data]);

  return (
    <>
      <div className="mt-4">
        <div>{t("Users.applicationRolesDescription")}</div>
      </div>

      {(isButtonAppRoles || isButtonAdminRoles) &&
        <BoxFull className="mt-4 mb-3 px-0 py-0 roles-actions-boxfull" variant="tertiary">
          <div className="subheader-buttons-roles">
            {isButtonAppRoles &&
              <AccessButton
                id='roles-edit-access-button'
                icon="iu iu-icon-edit"
                label={t("Users.goManagementApplication")}
                onClick={() => navigate("/applications")}
              />
            }
            {isButtonAdminRoles &&
              <AccessButton
                id='roles-edit-access-button'
                icon="iu iu-icon-edit"
                label={t("Users.goManagementRoles")}
                onClick={() => navigate("/adminroles")}
              />
            }
          </div>
        </BoxFull>
      }

      <div className="tenant-accordions mt-5">
        {applicationRolesData.length == 1 &&
          <BoxFull style={{ padding: '1.5rem !important' }} >
            <DataTable
              header={
                <TableHeader
                  title={`${t("Users.applicationRoles")}
                              ( ${(selectedApplicationRoles && selectedApplicationRoles
                      ?.filter((item: any) => item.tenantId === applicationRolesData[0]?.id)
                      ?.length) || 0
                    } )
                            `}
                  columns={columnsApps}
                  selectedColumns={selectedColumnsApps}
                  toggleColumns={setSelectedColumnsApps}
                  filters={filtersApps}
                  setFilters={setFiltersApps}
                />
              }
              currentPageReportTemplate={t(
                "ConsoleTranslations.rowsPaginatorTemplateLabel"
              )}
              paginatorRight={t('ConsoleTranslations.itemsTextDatatableLabel')}
              emptyMessage={t("TableActions.emptyMessage")}
              rows={10}
              size="normal"
              editMode="row"
              dataKey="id"
              sortField="functionality"
              className="tableroles"
              sortOrder={1}
              filters={filtersApps}
              value={applicationRolesData[0].segmentsFormated}
              scrollable
              resizableColumns
              expandedRows={expandedRows}
              onRowToggle={(e: any) => setExpandedRows(e.data)}
              rowExpansionTemplate={rowExpansionTemplate1}
            >
              {selectedColumnsApps.map((col) => (
                <Column
                  key={col.field}
                  field={col.field}
                  header={col.header}
                  sortable={!(col.field === "actions" || col.field === "select")}
                  align={col.field === "actions" ? "center" : "left"}
                  headerStyle={col.field === "actions"
                    ? { justifyContent: "center" }
                    : undefined}
                  style={{
                    boxShadow: (col.field === "actions")
                      ? "rgb(230 230 230 / 55%) -2px 0px, rgb(235 235 235 / 55%) -3px 0px, rgb(240 240 240 / 45%) -5px 0px"
                      : undefined,
                    maxWidth: (col.field === "actions") ? "132px" : undefined,
                    whiteSpace: "normal",
                    minWidth: col.field === "select" ? "10px !important" : (col.field === "actions") ? "8rem" : "3rem !important",
                  }}
                  frozen={col.field === "actions" ? true : false}
                  alignFrozen={col.field === "actions" ? "right" : undefined}
                  expander={col.field === "select" && allowExpansion}
                />
              ))}
            </DataTable>
          </BoxFull>}
        {applicationRolesData.length > 1 && applicationRolesData.map((tenant: any) => (
          <Accordion
            activeIndex={tenant.id}
            id="accordion"
            items={[
              {
                children: (
                  <>
                    {tenant.segments ? (
                      <DataTable
                        header={
                          <TableHeader
                            title={`${t("Users.applicationRoles")}
                              ( ${(selectedApplicationRoles && selectedApplicationRoles
                                ?.filter((item: any) => item.tenantId === tenant?.id)
                                ?.length) || 0
                              } )
                            `}
                            columns={columnsApps}
                            selectedColumns={selectedColumnsApps}
                            toggleColumns={setSelectedColumnsApps}
                            filters={filtersApps}
                            setFilters={setFiltersApps}
                          />
                        }
                        currentPageReportTemplate={t(
                          "ConsoleTranslations.rowsPaginatorTemplateLabel"
                        )}
                        paginatorRight={t('ConsoleTranslations.itemsTextDatatableLabel')}
                        emptyMessage={t("TableActions.emptyMessage")}
                        rows={10}
                        size="normal"
                        editMode="row"
                        dataKey="id"
                        sortField="functionality"
                        className="tableroles"
                        sortOrder={1}
                        filters={filtersApps}
                        value={tenant.segmentsFormated}
                        scrollable
                        resizableColumns
                        expandedRows={expandedRows}
                        onRowToggle={(e: any) => setExpandedRows(e.data)}
                        rowExpansionTemplate={rowExpansionTemplate1}
                      >
                        {selectedColumnsApps.map((col) => (
                          <Column
                            key={col.field}
                            field={col.field}
                            header={col.header}
                            sortable={!(col.field === "actions" || col.field === "select")}
                            align={col.field === "actions" ? "center" : "left"}
                            headerStyle={col.field === "actions"
                              ? { justifyContent: "center" }
                              : undefined}
                            style={{
                              boxShadow: (col.field === "actions")
                                ? "rgb(230 230 230 / 55%) -2px 0px, rgb(235 235 235 / 55%) -3px 0px, rgb(240 240 240 / 45%) -5px 0px"
                                : undefined,
                              maxWidth: (col.field === "actions") ? "132px" : undefined,
                              whiteSpace: "normal",
                              minWidth: col.field === "select" ? "10px !important" : (col.field === "actions") ? "8rem" : "3rem !important",
                            }}
                            frozen={col.field === "actions" ? true : false}
                            alignFrozen={col.field === "actions" ? "right" : undefined}
                            expander={col.field === "select" && allowExpansion}
                          />
                        ))}
                      </DataTable>
                    ) : (
                      <FormSkeleton length={8} />
                    )}
                  </>
                ),
                disabled: false,
                header: tenant?.name,
                key: tenant?.id,
              },
            ]}

          />
        ))}
      </div>

      <CustomDialog
        title={modalProps.title}
        visible={modalProps.visible}
        content={modalProps.content}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />
    </>
  );
};
